import type { ReactNode } from 'react';
import { useApp, type Melding } from '@/state/AppContext';
import {
  FoutIcoon,
  GeluktIcoon,
  GeenVerbindingIcoon,
  WaarschuwingIcoon,
} from '@/components/layout/Iconen';
import './ui.css';

export function Spinner({ groot = false }: { groot?: boolean }): JSX.Element {
  return <span className={`spinner${groot ? ' spinner--groot' : ''}`} aria-hidden="true" />;
}

/**
 * Wachten met de vorm van wat er komt, niet met een draaiend rondje.
 *
 * Een skelet laat zien dát er een lijst aankomt en hoe lang die ongeveer is.
 * Dat maakt het wachten korter dan een spinner die niets belooft.
 */
export function Laadvlak({
  tekst = 'Laden...',
  regels = 4,
}: {
  tekst?: string;
  regels?: number;
}): JSX.Element {
  return (
    <div className="skelet" role="status" aria-label={tekst}>
      {Array.from({ length: regels }, (_, i) => (
        <div key={i} className="skelet__regel" />
      ))}
      <span className="visueel-verborgen">{tekst}</span>
    </div>
  );
}

/**
 * Voortgang die je kunt uitrekenen, bijvoorbeeld het versturen van een foto.
 *
 * Bij minder beweging springt de balk per 5 procent in plaats van vloeiend te
 * groeien; een balk die stilstaat zou lijken alsof hij vastloopt.
 */
export function Voortgang({ percentage, label }: { percentage: number; label: string }): JSX.Element {
  const afgerond = Math.min(100, Math.max(0, Math.round(percentage)));
  return (
    <div className="voortgang">
      <div className="voortgang__kop">
        <span>{label}</span>
        <span className="voortgang__getal">{afgerond}%</span>
      </div>
      <div
        className="voortgang__baan"
        role="progressbar"
        aria-valuenow={afgerond}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div className="voortgang__vulling" style={{ width: `${afgerond}%` }} />
      </div>
    </div>
  );
}

/**
 * Een lege toestand vertelt wat er kán, niet dat er niets is.
 *
 * "Nog geen foto's" met daarachter waarom een foto helpt, en de knop om er een
 * te maken. Zonder dat laatste is het alleen een mededeling.
 */
export function LegeStaat({
  icoon,
  titel,
  children,
}: {
  icoon?: ReactNode;
  titel: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="leeg">
      {icoon && <span className="leeg__icoon">{icoon}</span>}
      <div className="leeg__titel">{titel}</div>
      {children}
    </div>
  );
}

/** Elk soort melding heeft een eigen icoon; kleur alleen is niet genoeg. */
function meldingIcoon(soort: Melding['soort']): JSX.Element {
  switch (soort) {
    case 'succes':
      return <GeluktIcoon />;
    case 'fout':
      return <FoutIcoon />;
    case 'waarschuwing':
      return <WaarschuwingIcoon />;
    default:
      return <GeenVerbindingIcoon />;
  }
}

/**
 * Meldingen, boven de onderbalk zodat ze op een telefoon zichtbaar blijven.
 *
 * De toon is: wat er is gebeurd en wat de app zelf al doet — niet wat de coach
 * fout deed. "Versturen is niet gelukt, het staat veilig op dit toestel" in
 * plaats van een foutcode.
 */
export function Meldingen(): JSX.Element | null {
  const { meldingen, sluitMelding } = useApp();
  if (meldingen.length === 0) return null;

  return (
    <div className="meldingen" role="status" aria-live="polite">
      {meldingen.map((melding) => (
        <div key={melding.id} className={`melding melding--${melding.soort}`}>
          <span className="melding__icoon">{meldingIcoon(melding.soort)}</span>
          <span className="melding__tekst">{melding.tekst}</span>
          <button
            type="button"
            className="melding__sluit"
            onClick={() => sluitMelding(melding.id)}
            aria-label="Melding sluiten"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
}
